import { useState } from "react";
import { KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function ForcePasswordChange() {
  const { reload, logout, user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) return setError("The two new passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      await reload();
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-12">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="force-password-change-form">
        <div className="mb-6 flex h-10 w-10 items-center justify-center rounded bg-primary text-primary-foreground">
          <KeyRound className="h-5 w-5" />
        </div>
        <div className="overline">Security</div>
        <h1 className="mt-1 font-display text-3xl font-bold">Set a new password</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {user?.email} is using a temporary password. Choose a new one to continue — at least 10
          characters with letters and numbers.
        </p>

        <div className="mt-8 space-y-4">
          <div>
            <Label htmlFor="cur">Temporary password</Label>
            <Input
              id="cur"
              data-testid="change-current-password-input"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="new">New password</Label>
            <Input
              id="new"
              data-testid="change-new-password-input"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="cnf">Confirm new password</Label>
            <Input
              id="cnf"
              data-testid="change-confirm-password-input"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              className="mt-1.5"
            />
          </div>
          {error && (
            <div
              data-testid="change-password-error"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </div>
          )}
          <Button data-testid="change-password-submit" type="submit" disabled={busy} className="w-full gap-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Update password & continue
          </Button>
          <Button type="button" variant="ghost" className="w-full" onClick={logout}>
            Sign out
          </Button>
        </div>
        <div className="mt-8 flex items-start gap-2 rounded-md border border-border bg-secondary/60 p-3 text-xs text-muted-foreground">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
          Passwords are stored as bcrypt hashes. No one, including support, can read your password.
        </div>
      </form>
    </div>
  );
}

export function ResetPassword() {
  const params = new URLSearchParams(window.location.search);
  const [token, setToken] = useState(params.get("token") || "");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) return setError("The two passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token: token.trim(), new_password: next });
      setDone(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-sm" data-testid="reset-password-page">
        <div className="overline">Password reset</div>
        <h1 className="mt-1 font-display text-3xl font-bold">Choose a new password</h1>
        {done ? (
          <div className="mt-6">
            <div
              data-testid="reset-password-success"
              className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700"
            >
              Password reset. You can now sign in.
            </div>
            <Button className="mt-4 w-full" onClick={() => (window.location.href = "/")}>
              Go to sign in
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="tok">Reset token</Label>
              <Input
                id="tok"
                data-testid="reset-token-input"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="np">New password</Label>
              <Input
                id="np"
                data-testid="reset-new-password-input"
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="cp">Confirm password</Label>
              <Input
                id="cp"
                data-testid="reset-confirm-password-input"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                className="mt-1.5"
              />
            </div>
            {error && (
              <div
                data-testid="reset-password-error"
                className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {error}
              </div>
            )}
            <Button data-testid="reset-password-submit" type="submit" disabled={busy} className="w-full gap-2">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Reset password
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
